<!-- FILE: components/Table/TableCell.vue -->
<template>
  <td
    :class="cellClasses"
    :style="cellStyle"
    :title="cellTitle"
    @mouseover="$emit('mouseover')"
    @mouseleave="$emit('mouseleave')"
    @click="$emit('click', $event)"
  >
    <!-- EDITABLE CELLS (description, weight, steps, responsibles) -->
    <span
      v-if="header.editable"
      :class="{
        'left-justified': header.value === 'description' || header.value.includes('responsible'),
      }"
    >
      <!-- When NOT showing edit dialog -->
      <span v-if="!item.showEditDialog && !item.isChild">
        {{ editableValueDisplay }}
      </span>

      <!-- When showing edit dialog OR is a child item -->
      <span v-else>
        <!-- STEP CELLS (special handling) -->
        <template v-if="header.value.includes('step')">
          <!-- Child Step Progress Percentage -->
          <span v-if="item.isChild">
            {{ formatSubProjectStepValue(item.id, header.value) }}
          </span>

          <!-- Parent/Regular Step Status (clickable) -->
          <span v-else @click.stop="$emit('status-change')" style="cursor: pointer;">
            {{ item[header.value] }}
          </span>
        </template>

        <!-- OTHER EDITABLE CELLS (Description, Weight, Responsible) -->
        <template v-else>
          <edit-dialog-table-body
            @handleChangeCell="(item, headerValue) => $emit('cell-save', item, headerValue)"
            @handleClose1="(item) => $emit('handleClose1', item)"
            @handleInputChanged="(value, param) => $emit('handleInputChange', value, param)"
            @handleOpen="(openedCell) => $emit('handleOpen', openedCell)"
            :item="item"
            :header="header"
          />
        </template>
      </span>
    </span>

    <!-- NON-EDITABLE CELLS (id, fulfillment) -->
    <span v-else>
      <!-- FULFILLMENT COLUMN - Progress Bar -->
      <span v-if="header.value === 'fulfillment'">
        <custom-progress-bar :value="numericValue" color="green" :height="tdHeight" reactive striped>
          {{ displayValue }}
        </custom-progress-bar>
      </span>

      <!-- ID COLUMN and other non-editable columns -->
      <div
        v-else
        :class="{
          'deliverable--number': header.value === 'id',
          'selected-row': isSelected,
          'has-children': hasChildren && (header.value === 'id' || header.value === 'description'),
        }"
        :style="idIndentStyle"
      >
        {{ item[header.value] }}
      </div>
    </span>
  </td>
</template>

<script>
import CustomProgressBar from "./CustomProgressBar.vue";
import EditDialogTableBody from "./EditDialogTableBody.vue";
import { getCellBackgroundColor } from "../../utils/table-helpers";
import { toNumber, toPercentNumber } from "../../utils/rational";

export default {
  name: "TableCell",
  components: {
    CustomProgressBar,
    EditDialogTableBody,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    header: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    expandedWidth: {
      type: Boolean,
      default: true,
    },
    tdHeight: {
      type: Number,
      default: 16,
    },
    indentLevel: {
      type: Number,
      default: 0,
    },
    subProjectSteps: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    cellClasses() {
      return {
        "expanded-td": this.expandedWidth,
        "fulfillment-td": this.header.value == "fulfillment",
      };
    },
    cellStyle() {
      return {
        backgroundColor: getCellBackgroundColor(this.item[this.header.value]),
      };
    },
    editableValueDisplay() {
      if (this.header.value === "weight") {
        return toNumber(this.item.weight);
      }
      return this.item[this.header.value];
    },
    cellTitle() {
      if (this.hasChildren && (this.header.value === "id" || this.header.value === "description")) {
        return "Double-click to open subproject";
      }
      if (this.header.value === "weight") {
        return "";
      }
      if (this.header.value === "fulfillment") {
        const value = this.item[this.header.value];
        if (value === "n.rel.") return "n.rel.";
        if (typeof value === "number") return `${Math.round(value)}%`;
        return `${Math.round(toPercentNumber(value))}%`;
      }
      if (this.expandedWidth && !this.header.value.includes("step") && isNaN(this.item[this.header.value])) {
        return this.item[this.header.value];
      }
      return "";
    },
    numericValue() {
      const value = this.item[this.header.value];
      if (this.header.value === "fulfillment") {
        if (value === "n.rel.") return 0;
        if (typeof value === "number") return value;
        return toPercentNumber(value);
      }
      return typeof value === "number" ? value : 0;
    },
    displayValue() {
      const value = this.item[this.header.value];
      if (this.header.value === "fulfillment") {
        if (value === "n.rel.") return "n.rel.";
        if (typeof value === "number") return `${Math.round(value)}%`;
        return `${Math.round(toPercentNumber(value))}%`;
      }
      return value;
    },
    hasChildren() {
      return (Array.isArray(this.item.children) && this.item.children.length > 0) || this.item.isGrouped;
    },
    idIndentStyle() {
      if (this.header.value !== "id" && this.header.value !== "description") return {};
      return { paddingLeft: `${this.indentLevel * 16}px` };
    },
  },
  methods: {
    formatSubProjectStepValue(itemId, stepKey) {
      const value = this.subProjectSteps?.[itemId]?.[stepKey];
      if (value === "n.rel.") return "n.rel.";
      if (value === undefined || value === null) return "0%";

      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "n.rel.";
      return `${Math.round(numeric)}%`;
    },
    getCellBackgroundColor(status) {
      const colors = {
        open: "#FF0000",
        wip: "orange",
        "n.rel.": "#778899",
        closed: "#7bea7b",
      };
      return colors[status] || null;
    },
    handleChangeCell(item, headerValue) {
      this.$emit("cell-save", item, headerValue);
    },
    // handleClose1(item) {
    //   this.$emit("close1", item);
    // },
    // handleInputChanged(value, param) {
    //   // Handled by parent
    // },
    // handleOpen(openedCell) {
    //   this.$emit("handleOpen", openedCell);
    // },
  },
};
</script>

<style scoped>
td {
  /* padding: 4px 8px; */
  border: 1px solid rgba(0, 0, 0, 0.12);
  text-align: center;
  vertical-align: middle;
}

.left-justified {
  text-align: left !important;
}

.expanded-td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 20ch;
}

.deliverable--number {
  cursor: pointer;
  font-weight: 500;
  /* color: #1976d2; */
}

/* .deliverable--number:hover {
  text-decoration: underline;
} */

.selected-row {
  background-color: lightgray !important;
}

.fulfillment-td {
  padding: 0;
}

.has-children {
  text-decoration: underline;
  cursor: pointer;
}
</style>
